import { DateTime } from 'luxon';
import { FC } from 'react';
import styled, { css } from 'styled-components';

import { ReactComponent as HyperlinkIcon } from '@/assets/icons/hyperlink.svg';
import Text from '@/components/Text';
import { DQCommentsCheckerPost } from '@/pages/CommentsChecker';

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const Label = styled(Text).attrs({ $type: 'smallHeadline', $color: 'gray' })`
  margin-bottom: 8px;
`;

const Details = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const DetailsBlock = styled.div`
  padding: 12px 0;
  &:nth-child(even) {
    padding-left: 24px;
    border-left: 1px solid ${({ theme }) => theme.colors.lightGray};
  }
`;

const Wrap = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px 0px #00000014;
  border-radius: 4px;
  margin-bottom: 32px;
`;

const Header = styled.div`
  padding: 24px;
  display: flex;
  align-items: center;
`;

const HeaderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Button = styled.button.attrs({ type: 'button' })`
  height: 38px;
  min-width: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 4px;
  border-width: 1px;
  border-style: solid;
  transition: 0.2s ease-out all;
  background-color: transparent;
`;

export const ActionButton = styled(Button)<{ $secondary?: boolean }>`
  padding: 0 32px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Manrope', sans-serif;

  /* GRAY variation */
  ${({ theme, $secondary = false }) =>
    $secondary
      ? css`
          color: ${theme.colors.black};
          border-color: ${theme.colors.lightGray};

          &:hover {
            border-color: ${theme.colors.gray};
          }
        `
      : css`
          color: ${({ theme }) => theme.colors.purple};
          border-color: ${({ theme }) => theme.colors.purple};

          &:hover {
            color: ${({ theme }) => theme.colors.white};
            background: ${({ theme }) => theme.colors.purple};
          }
        `}

  &:disabled {
    color: ${({ theme }) => theme.colors.gray};
    border-color: ${({ theme }) => theme.colors.lightGray};
    cursor: not-allowed;
    background: transparent;
  }
`;

const Body = styled.div`
  padding: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const LinkText = styled(Text)`
  display: flex;
  gap: 8px;
  align-items: center;
`;

interface Props {
  data: DQCommentsCheckerPost;
}

const PostInfoCard: FC<Props> = ({ data }) => {
  const { post } = data;

  return (
    <Wrap>
      <Header>
        <HeaderContent>
          <Text $type="button">Post Information</Text>
        </HeaderContent>
      </Header>
      <Body>
        <Rows>
          <Details>
            <DetailsBlock>
              <Label>Post ID</Label>
              <LinkText
                $type="captionSemibold"
                as="a"
                href={`https://facebook.com/${post.external_id}`}
                target="_blank"
              >
                <HyperlinkIcon />
                {post.external_id}
              </LinkText>
            </DetailsBlock>
            <DetailsBlock>
              <Label>Message</Label>
              <Text $type="captionSemibold">{post.message ?? '-'}</Text>
            </DetailsBlock>
            <DetailsBlock>
              <Label>Page</Label>
              <Text $type="captionSemibold">{post.channel.name ?? '-'}</Text>
            </DetailsBlock>
            <DetailsBlock>
              <Label>Published at</Label>
              <Text $type="captionSemibold">
                {DateTime.fromISO(post.published_at).toFormat(
                  'yyyy/LL/dd   HH:mm',
                )}
              </Text>
            </DetailsBlock>
            <DetailsBlock>
              <Label>Comments checked</Label>
              <Text $type="captionSemibold">{data.total_comments_count}</Text>
            </DetailsBlock>
            <DetailsBlock>
              <Label>Comments deleted</Label>
              <Text $type="captionSemibold">{data.deleted_comments_count}</Text>
            </DetailsBlock>
          </Details>
        </Rows>
      </Body>
    </Wrap>
  );
};

export default PostInfoCard;
